import {
  Component,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  ApplicationRef,
  inject,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core'
import { BarcodeUtil } from 'depoto-core/src/utils'
import { Depot, Supplier, Product, Vat, Producer, ProductMove, ProductDepot, Currency } from 'depoto-core/src/entities'
import { FormRowTextareaComponent } from '../../_common/form-row-textarea/form-row-textarea.component'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { CoreService, NotificationService } from '../../../services'
import { generateUuidV4 } from 'depoto-core/src/utils/UuidUtil'
import { TranslateService } from '@ngx-translate/core'
import { isObjectEmpty } from 'src/app/utils'
import { PaymentService } from '../../../services/entity-services/payment.service'
import { injectQuery } from '@tanstack/angular-query-experimental'
import { findEquivalentQueuedProductMove, QueuedProductMoveCandidate } from './queued-product-move-equivalence.util'

declare let disableBarcodeScanner: boolean

@Component({
  selector: 'modal-stock-operation-card',
  templateUrl: 'modal-stock-operation-card.component.html',
  styleUrls: ['modal-stock-operation-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalStockOperationCardComponent implements OnInit {
  private paymentService = inject(PaymentService)
  @ViewChild('modal') public modal: ModalContainerComponent
  @ViewChild('inputNote') public inputNoteComponent: FormRowTextareaComponent
  @Input() public product: Product
  @Input() public type: string
  @Input() public depotFrom: number
  @Input() public depotTo: number
  @Input() public isCreatingProduct: boolean
  @Input() public alwaysLoadSelectData: boolean
  @Input() public hideButton: boolean
  @Input() public initDateDUZP: string
  // Green "stock in" button instead of "select" (purchase order rows).
  @Input() public isStockInButton = false
  // Starting values for a new stock in; when not set, the product's purchase price and 1 piece are used.
  @Input() public initQuantity: number | null = null
  @Input() public initPurchasePrice: number | null = null
  @Input() public initCurrency: string | null = null
  // When bound, it replaces the last-used supplier from other pages; null falls back to the product's supplier.
  @Input() public initSupplier: number | null | undefined = undefined
  // Lets the page stop the modal from opening (e.g. unsaved changes); return false to cancel.
  @Input() public canOpen: (() => boolean) | null = null
  @Input() public operationToUpdate: ProductMove
  @Input() public depots: Depot[] = []
  @Input() public suppliers: Supplier[] = []
  @Input() public producers: Producer[] = []
  @Input() public vats: Vat[] = []
  @Input() moves: ProductMove[] = []
  @Input() movesOriginPositions: { productMoveUuid: string | number; originPosition: string }[] = []
  @Output() movesOriginPositionsChange: EventEmitter<any> = new EventEmitter()
  @Input() productDepot: ProductDepot
  @Input() depotQuantity: number
  @Input() dataCyAttribute: string
  @Output() public result: EventEmitter<any> = new EventEmitter()
  @Output() public operationUpdated: EventEmitter<ProductMove> = new EventEmitter()
  @Output() openModalSerialNumbers: EventEmitter<ProductMove> = new EventEmitter()
  submitted = false
  operation: ProductMove
  selectedSupplier: number
  purchasePrice
  selectedCurrency = 'CZK'
  dateDUZP: string
  sellPrice = ''
  quantity = 1
  note = ''
  batch = ''
  expirationDate = ''
  position1 = ''
  position2 = ''
  position3 = ''
  resultMessage = ''
  isCreatedProduct = false
  // isQuantityLocked = false
  loading = false
  keyEventSubscriptions: any = {}
  headerTitle: string
  public modalHeaderIcon: string
  public modalTitle: string

  productDepots = []
  selectCustomPositions = false

  readonly queryCurrencies = injectQuery(() => ({
    queryKey: ['currencies', 'options'],
    queryFn: async () => {
      return await this.paymentService.getCurrencies({}, { id: null, name: null, ratio: null }).then(result => {
        return result.items
      })
    },
  }))

  constructor(
    private core: CoreService,
    private translateService: TranslateService,
    private notificationService: NotificationService
  ) {
    effect(() => {
      this.queryCurrencies.data()
    })
  }

  ngOnInit() {
    this.getTotalQuantityStock()
    this.getSelectData()
    this.getModalData()
    this.setDateDUZP()
    // todo will i need to effect this here?
    // this.getCurrencies()
  }

  setDateDUZP() {
    if (!!this.initDateDUZP && (!this.dateDUZP || (this.dateDUZP && !this.dateDUZP.length))) {
      this.dateDUZP = this.initDateDUZP
    }
  }

  selectCustomPositionChange(event) {
    this.selectCustomPositions = event
    // this.isQuantityLocked = !!event
    // this.appRef.tick()
  }

  clearOptions() {
    // this.isQuantityLocked = false
    this.selectCustomPositions = false
    // this.operationToUpdate = null
    // this.isCreatedProduct = false
    // this.isCreatingProduct = false
  }

  private getModalData() {
    if (this.isCreatedProduct) {
      if (this.type === 'in') {
        this.modalHeaderIcon = 'fa-truck'
        if (this.product) {
          this.modalTitle = this.translateService.instant('modal-stock-operation-card.load', {
            name: this.product.name,
          })
        }
      } else if (this.type === 'out') {
        this.modalHeaderIcon = 'fa-upload'
        if (this.product) {
          this.modalTitle = this.translateService.instant('modal-stock-operation-card.unload', {
            name: this.product.name,
          })
        }
      } else if (this.type === 'transfer') {
        this.modalHeaderIcon = 'fa-exchange'
        if (this.product) {
          this.modalTitle = this.translateService.instant('modal-stock-operation-card.transfer', {
            name: this.product.name,
          })
        }
      }
    } else {
      this.modalHeaderIcon = 'fa-file'
      this.isCreatingProduct ? (this.modalTitle = 'product.create') : (this.modalTitle = 'product.update')
    }
  }

  getTotalQuantityStock() {
    this.depotQuantity = this.productDepots.reduce((currentTotal, pDepot) => pDepot.quantityStock + currentTotal, 0)
  }

  createProduct(): void {
    this.loading = true
    this.submitted = true
    if (
      !this.product.name ||
      this.product?.sellPrice < 0 ||
      (!this.product?.sellPrice && this.product?.sellPrice !== 0)
    ) {
      this.loading = false
      return
    }
    if (!this.product?.vat?.id) this.vats.filter(v => v.default)[0]
    this.core.services.product
      .create(this.product, {
        id: null,
        name: null,
        code: null,
        ean: null,
        sellPrice: null,
        beforeSellPrice: null,
        vat: {
          id: null,
          name: null,
        },
      })
      .then(res => {
        if (res && res.id) {
          this.product = new Product(res)
          this.isCreatedProduct = true
          this.notificationService.success(this.translateService.instant('alert.product.created'))

          this.isCreatingProduct = false
          this.getSelectData()
          this.getModalData()
          this.loading = false
        } else {
          this.result.emit(res)
          this.loading = false
        }
        // this.onHideChildModal()
      })
  }

  updateProduct(): void {
    this.loading = true
    this.submitted = true
    this.core.services.product
      .update(this.product)
      .then(res => {
        if (res && res.id) {
          this.product = new Product(res)
          this.result.emit(this.product)
        } else {
          this.result.emit(res)
        }
        this.onHideChildModal()
      })
      .catch(err => this.result.emit(err))
  }

  setPosition(positions) {
    this.position1 = positions.position1
    this.position2 = positions.position2
    this.position3 = positions.position3
  }

  private queuedMoveCandidate(supplier: number): QueuedProductMoveCandidate {
    return {
      productId: this.product.id,
      batch: this.batch,
      expirationDate: this.expirationDate,
      position1: this.position1,
      position2: this.position2,
      position3: this.position3,
      supplier,
      purchaseCurrency: this.selectedCurrency,
      purchaseCurrencyDate: this.dateDUZP,
      note: this.note,
      purchasePrice: this.purchasePrice,
    }
  }

  createOperation(): void {
    if (this.type === 'in' && (!this.selectedSupplier || this.selectedSupplier < 1)) {
      alert(this.translateService.instant('modal-stock-operation-card.supplier.error'))
      return
    }
    if (this.quantity <= 0) {
      alert(this.translateService.instant('modal-stock-operation-card.quantity.error'))
      return
    }
    if (this.type === 'transfer' && (this.purchasePrice < 0 || (!this.purchasePrice && this.purchasePrice !== 0))) {
      alert(this.translateService.instant('modal-stock-operation-card.price.error'))
      return
    }
    if (
      this.type === 'out' &&
      Number(
        this.productDepots.reduce((currentTotal, pDepot) => {
          return Number(pDepot.amountToUnload) + Number(currentTotal)
        }, 0)
      ) !== Number(this.quantity)
    ) {
      alert(this.translateService.instant('modal-stock-operation-card.quantity-not-match'))
      this.onHideChildModal()
      return
    }
    if (
      this.type === 'transfer' &&
      this.moves.some(m => {
        return (
          Number(m.productDepotFrom.id) === Number(this.productDepot?.id) &&
          Number(this.quantity) + Number(m.amount) > Number(m.productDepotFrom.quantityStock)
        )
      })
    ) {
      alert(this.translateService.instant('modal-stock-operation-card.alert.max-quantity-transfer'))
      return
    }
    let wasQuantityChanged = false
    let op: ProductMove
    const affectedMoves: ProductMove[] = []
    switch (this.type) {
      case 'in': {
        const sameItemAlreadyInMovesIn = findEquivalentQueuedProductMove(
          this.moves,
          this.queuedMoveCandidate(this.selectedSupplier),
          this.operationToUpdate
        )
        if (sameItemAlreadyInMovesIn) {
          sameItemAlreadyInMovesIn.amount = Number(sameItemAlreadyInMovesIn.amount) + Number(this.quantity)
        } else if (this.operationToUpdate) {
          if (this.operationToUpdate.amount !== this.quantity) {
            wasQuantityChanged = true
          }
          this.operationToUpdate.amount = this.quantity
          this.operationToUpdate.depotFrom = this.depotFrom
          this.operationToUpdate.purchasePrice = Number(this.purchasePrice)
          this.operationToUpdate.purchasePriceSum = Number(this.purchasePrice) * Number(this.quantity)
          this.operationToUpdate.purchaseCurrency = this.selectedCurrency
          this.operationToUpdate.batch = this.batch
          this.operationToUpdate.expirationDate = this.expirationDate
          this.operationToUpdate.position1 = this.position1
          this.operationToUpdate.position2 = this.position2
          this.operationToUpdate.position3 = this.position3
          this.operationToUpdate.note = this.note
        } else {
          op = new ProductMove({
            depotTo: this.depotTo,
            supplier: this.selectedSupplier,
            amount: Number(this.quantity),
            purchasePrice: Number(this.purchasePrice),
            purchasePriceSum: Number(this.purchasePrice) * Number(this.quantity),
            note: this.note,
            product: this.product,
            batch: this.batch,
            expirationDate: this.expirationDate,
            position1: this.position1,
            position2: this.position2,
            position3: this.position3,
            purchaseCurrency: this.selectedCurrency,
            serialNumbers: this.operationToUpdate?.serialNumbers,
          })
          this.moves.push(op)
          if (this.dateDUZP) {
            op.purchaseCurrencyDate = this.dateDUZP
          }
        }
        break
      }
      case 'out': {
        const sameItemAlreadyInMovesOut = findEquivalentQueuedProductMove(
          this.moves,
          this.queuedMoveCandidate(this.selectedSupplier),
          this.operationToUpdate
        )
        if (sameItemAlreadyInMovesOut) {
          sameItemAlreadyInMovesOut.amount = Number(sameItemAlreadyInMovesOut.amount) + Number(this.quantity)
          affectedMoves.push(sameItemAlreadyInMovesOut)
        } else if (this.operationToUpdate) {
          this.operationToUpdate.amount = this.quantity
          this.operationToUpdate.depotFrom = this.depotFrom
          this.operationToUpdate.purchasePrice = Number(this.purchasePrice)
          this.operationToUpdate.purchasePriceSum = Number(this.purchasePrice) * Number(this.quantity)
          this.operationToUpdate.purchaseCurrency = this.selectedCurrency
          this.operationToUpdate.batch = this.batch
          this.operationToUpdate.expirationDate = this.expirationDate
          this.operationToUpdate.position1 = this.position1
          this.operationToUpdate.position2 = this.position2
          this.operationToUpdate.position3 = this.position3
          affectedMoves.push(this.operationToUpdate)
        } else {
          this.productDepots.forEach(pDepot => {
            if (pDepot.amountToUnload > 0) {
              op = new ProductMove({
                depotFrom: this.depotFrom,
                amount: Number(pDepot.amountToUnload),
                purchasePrice: Number(pDepot.purchasePrice),
                purchasePriceSum: Number(pDepot.purchasePrice) * Number(pDepot.amountToUnload),
                product: this.product,
                productDepot: pDepot.id,
                batch: pDepot.batch,
                expirationDate: pDepot.expirationDate,
                position1: pDepot.position1,
                position2: pDepot.position2,
                position3: pDepot.position3,
                serialNumbers: this.operationToUpdate?.serialNumbers,
              })
              this.moves.push(op)
              affectedMoves.push(op)
            }
          })
        }
        break
      }
      case 'transfer': {
        console.log('transfer')
        const sourceProductDepot = this.productDepot ?? this.operationToUpdate?.productDepotFrom
        const transferSupplier = sourceProductDepot?.supplier?.id ?? this.operationToUpdate?.supplier
        const sameItemAlreadyInMovesTransfer = findEquivalentQueuedProductMove(
          this.moves,
          this.queuedMoveCandidate(transferSupplier),
          this.operationToUpdate
        )
        if (sameItemAlreadyInMovesTransfer) {
          sameItemAlreadyInMovesTransfer.amount = Number(sameItemAlreadyInMovesTransfer.amount) + Number(this.quantity)
        } else if (this.operationToUpdate) {
          this.operationToUpdate.amount = this.quantity
          this.operationToUpdate.depotFrom = this.depotFrom
          this.operationToUpdate.purchasePrice = Number(this.purchasePrice)
          this.operationToUpdate.purchasePriceSum = Number(this.purchasePrice) * Number(this.quantity)
          this.operationToUpdate.purchaseCurrency = this.selectedCurrency
          this.operationToUpdate.batch = this.batch
          this.operationToUpdate.expirationDate = this.expirationDate
          this.operationToUpdate.position1 = this.position1
          this.operationToUpdate.position2 = this.position2
          this.operationToUpdate.position3 = this.position3
          this.operationToUpdate.supplier = transferSupplier
          this.operationToUpdate.productDepotFrom = sourceProductDepot
          this.operationToUpdate.productDepot = sourceProductDepot?.id ?? this.operationToUpdate.productDepot
          this.operationToUpdate.note = this.note
        } else {
          op = new ProductMove({
            uuid: generateUuidV4(),
            depotFrom: this.depotFrom,
            depotTo: this.depotTo,
            productDepotFrom: sourceProductDepot,
            productDepot: sourceProductDepot?.id,
            supplier: transferSupplier,
            amount: Number(this.quantity),
            purchasePrice: Number(this.purchasePrice),
            purchasePriceSum: Number(this.purchasePrice) * Number(this.quantity),
            purchaseCurrency: this.selectedCurrency,
            note: this.note,
            product: this.product,
            batch: this.batch,
            expirationDate: this.expirationDate,
            position1: this.position1,
            position2: this.position2,
            position3: this.position3,
            originPosition: sourceProductDepot?.position,
          })
          this.moves.push(op)
          const movesOriginPosition = { productMoveUuid: op.uuid, originPosition: sourceProductDepot?.position }
          this.movesOriginPositions.push(movesOriginPosition)
        }
        break
      }
    }
    if (this.operationToUpdate) {
      this.operationUpdated.emit(this.operationToUpdate)
    } else {
      this.result.emit(this.moves)
      this.movesOriginPositionsChange.emit(this.movesOriginPositions)
    }
    if (this.type === 'in') {
      if (
        this.product?.requiredSerialNumbers &&
        (!this.operationToUpdate || (this.operationToUpdate && wasQuantityChanged))
      ) {
        this.openModalSerialNumbers.emit(this.operationToUpdate ?? op)
      }
    } else if (this.type === 'out') {
      affectedMoves
        .filter(move => move.product?.requiredSerialNumbers)
        .forEach(move => {
          this.openModalSerialNumbers.emit(move)
        })
    }
    // A supplier given by the page (purchase order) is not remembered as the last-used one for other pages.
    if (this.initSupplier === undefined) {
      window['selectedSupplier'] = this.selectedSupplier
    }
    this.onHideChildModal()
  }

  async fillNextEan() {
    this.product.ean = await this.core.services.product.getNextEan()
  }

  showProductOnChildModal(product?: Product, isCreatingProduct?: boolean, productEan?: string): void {
    this.isCreatingProduct = !!isCreatingProduct
    if (product && !isCreatingProduct) {
      this.product = product
    }
    this.showChildModal(productEan)
  }

  showProductDepotOnChildModal(productDepot?: ProductDepot, product?: Product): void {
    this.productDepot = productDepot
    this.product = product
    this.showChildModal()
  }

  public async showChildModal(productEan?: string) {
    if (this.canOpen && !this.canOpen()) {
      return
    }
    this.loading = true
    if (this.initSupplier !== undefined) {
      this.selectedSupplier = this.initSupplier
    } else if (window['selectedSupplier'] && !this.selectedSupplier) {
      this.selectedSupplier = window['selectedSupplier']
    }
    if (this.product && this.product.supplier && this.product.supplier.id > 0 && !this.selectedSupplier) {
      this.selectedSupplier = this.product.supplier.id
    }
    if (!this.isCreatingProduct) {
      this.keyEventSubscriptions.keyEnter = this.core.services.keyEvent.onKeyEnter.subscribe().then(() => {
        if (
          this.modal.isShown() &&
          this.inputNoteComponent &&
          this.inputNoteComponent.inputEl &&
          this.inputNoteComponent.inputEl.nativeElement &&
          this.inputNoteComponent.inputEl.nativeElement !== document.activeElement
        ) {
          // if textarea is not in focus
          this.createOperation()
        }
      })
      this.keyEventSubscriptions.keyEsc = this.core.services.keyEvent.onKeyEsc.subscribe().then(() => {
        this.onHideChildModal()
      })
      this.keyEventSubscriptions.scannedPositions = this.core.services.keyEvent.onScannedPositions
        .subscribe()
        .then(positions => {
          this.position1 = positions[0]
          this.position2 = positions[1]
          this.position3 = positions[2]
          // this.appRef.tick()
        })
    }
    disableBarcodeScanner = true
    if (
      (this.type === 'in' && !this.depotTo) ||
      (this.type === 'out' && !this.depotFrom) ||
      (this.type === 'transfer' && (!this.depotFrom || !this.depotTo))
    ) {
      alert(this.translateService.instant('modal-stock-operation-card.select-warehouse'))
      this.loading = false
      disableBarcodeScanner = false
      return
    }

    if (this.type !== 'transfer' && this.product?.id) {
      await this.fetchProductDepots()
    }

    try {
      if (!this.isCreatingProduct && this.product) {
        this.productDepots = Object.assign([], this.getProductDepots())
        if (this.type === 'out' || this.type === 'transfer') {
          this.productDepots = this.productDepots.filter(pDepot => Number(pDepot.depot.id) === Number(this.depotFrom))
        }
        this.isCreatedProduct = true
      } else if (this.isCreatingProduct) {
        this.product = new Product({ enabled: true })
        this.isCreatedProduct = false
        if (productEan) {
          setTimeout(() => {
            this.product.ean = productEan
          }, 50)
        }
      }
      if (this.operationToUpdate) {
        this.selectedSupplier = this.operationToUpdate.supplier
        this.purchasePrice = this.operationToUpdate.purchasePrice || 0
        this.sellPrice = this.operationToUpdate.sellPrice + '' // is not used!
        this.quantity = this.operationToUpdate.amount
        this.note = this.operationToUpdate.note
        this.batch = this.operationToUpdate.batch
        this.expirationDate = this.operationToUpdate.expirationDate
        this.position1 = this.operationToUpdate.position1
        this.position2 = this.operationToUpdate.position2
        this.position3 = this.operationToUpdate.position3
        if (this.operationToUpdate.purchaseCurrency) {
          this.selectedCurrency = this.operationToUpdate.purchaseCurrency
        }
        if (this.operationToUpdate.purchaseCurrencyDate) {
          this.dateDUZP = this.operationToUpdate.purchaseCurrencyDate
        }
      } else if (this.type === 'out') {
        if (this.moves.some(move => move.product.id === this.product.id)) {
          const pushedMovesWithSameProduct = this.moves.filter(move => move.product.id === this.product.id)
          this.quantity = 0
          this.productDepots.forEach(pDepot => {
            pushedMovesWithSameProduct.forEach(pushedMove => {
              if (pushedMove.productDepot === pDepot.id) {
                pDepot.amountToUnload = pushedMove.amount
                this.quantity = this.quantity + pDepot.amountToUnload
              }
            })
          })
        } else {
          this.quantity = 1
          this.productDepots.forEach(pDepot => {
            pDepot.amountToUnload = 0
          })
          if (this.productDepots?.length) {
            this.productDepots[0].amountToUnload = 1
          }
        }
      } else if (this.type === 'in') {
        this.purchasePrice = this.initPurchasePrice ?? (this.product.purchasePrice || 0)
        this.selectedCurrency = this.initCurrency || this.product.purchaseCurrency?.id
        this.quantity = Number(this.initQuantity) > 0 ? Number(this.initQuantity) : 1
      } else {
        this.quantity = 1
        this.batch = this.productDepot?.batch
        this.expirationDate = this.productDepot?.expirationDate
        this.purchasePrice = this.productDepot?.purchasePrice || 0
        this.selectedCurrency = this.product?.purchaseCurrency?.id
      }
      // this.getViewSettings()
      this.getSelectData()
      this.getModalData()
      this.setDateDUZP()
      this.getTotalQuantityStock()
      this.loading = false
      this.modal.showChildModal()
    } catch (e) {
      disableBarcodeScanner = false
      throw e
    }
  }

  getProductDepots() {
    return this.product.productDepots.filter(depot => depot.quantityStock > 0)
  }
  async fetchProductDepots() {
    // this.loading = true
    try {
      const r = await this.core.services.product.getById(this.product.id, {
        id: null,
        purchasePrice: null,
        supplier: { id: null, name: null },
        productDepots: {
          id: null,
          depot: { id: null, name: null, unavailablesUrl: null },
          supplier: { id: null, name: null },
          purchasePrice: null,
          quantityStock: null,
          quantityReservation: null,
          quantityAvailable: null,
          inventoryQuantityStock: null,
          batch: null,
          expirationDate: null,
          position: null,
          position1: null,
          position2: null,
          position3: null,
          created: null,
          updated: null,
        },
      })
      if (r?.productDepots) {
        if (this.product.productDepots) {
          this.product.productDepots = [...r.productDepots]
        } else {
          this.product = new Product({ ...this.product, productDepots: r.productDepots })
        }
      }
    } catch (err) {
      this.loading = false
      console.error(err)
    }
  }

  public onHideChildModal(): void {
    this.loading = false
    this.submitted = false
    // Use the container's focus-safe close path. Do not emit `hide` here:
    // this method is also the handler for that event.
    this.modal?.hideChildModal(false)
    if (!disableBarcodeScanner && isObjectEmpty(this.keyEventSubscriptions)) return

    disableBarcodeScanner = false

    for (const keyEvent in this.keyEventSubscriptions) {
      this.keyEventSubscriptions[keyEvent]?.unsubscribe()
      delete this.keyEventSubscriptions[keyEvent]
    }

    this.clearOptions()
    this.selectedSupplier = null
    // if (!this.isQuantityLocked) {
    this.quantity = 1
    // }
    this.note = ''
    this.batch = ''
    this.expirationDate = ''
    this.position1 = ''
    this.position2 = ''
    this.position3 = ''
  }

  // setViewSettings(): void {
  //   setTimeout(() => {
  //     // this.core.services.storage.set(this.keyService.getStorageKeyForEntity('stockOperationCardSupplier'), this.selectedSupplier)
  //     // never read
  //     this.core.services.storage.set(
  //       this.keyService.getStorageKeyForEntity('stockOperationCardIsLockedQuantity'),
  //       this.isQuantityLocked
  //     )
  //     this.core.services.storage.set(
  //       this.keyService.getStorageKeyForEntity('stockOperationCardLockedQuantity'),
  //       this.quantity
  //     )
  //   }, 100)
  // }

  getViewSettings(): void {
    // this.isQuantityLocked =
    //   this.core.services.storage.getSync(
    //     this.keyService.getStorageKeyForEntity('stockOperationCardIsLockedQuantity')
    //   ) || false
    // if (this.isQuantityLocked && !this.operationToUpdate) {
    //   this.quantity =
    //     this.core.services.storage.getSync(
    //       this.keyService.getStorageKeyForEntity('stockOperationCardLockedQuantity')
    //     ) || 1
    // }
  }

  getSelectData() {
    if (this.product && !this.product.vat) {
      this.vats.forEach((vat: Vat) => {
        if (vat.default) {
          setTimeout(() => {
            this.product.vat = vat
            /* this.core.services.storage.set(this.keyService.getStorageKeyForEntity('stockOperationCardSelectData'), {
              producers: this.producers,
              vats: this.vats,
              defaultVat: vat,
            })
            never read
            */
          }, 500) // Why setTimeout and why 500ms? Can't we set this.product = {...this.product, vat}?
          return
        }
      })
    }
  }

  selectVat(event): void {
    this.vats.forEach((vat: Vat) => {
      if (vat.id === Number(event)) {
        this.product.vat = vat
        return
      }
    })
  }

  selectProducer(event): void {
    this.producers.forEach((producer: Producer) => {
      if (producer.id === Number(event)) {
        this.product.producer = producer
        return
      }
    })
  }

  setParent(parent: Product): void {
    this.product.parent = parent
    this.product.sellPrice = parent.sellPrice
    this.product.producer = parent.producer
    this.product.vat = parent.vat
  }

  setEan(ean): void {
    // SHAME: this is small hack to ensure translation of the last character, untranslated by directive..
    this.product.ean = BarcodeUtil.translateBarcode(ean)
  }

  fillLastUsedValues(): void {
    if (this.product.productDepots?.length) {
      const depotsSorted = [...this.product.productDepots].filter(d => d.depot.id === Number(this.depotTo))
      depotsSorted.sort((a, b) => {
        if (a.id > b.id) {
          // todo by date and check why is not working??
          return -1
        } else if (a.id < b.id) {
          return 1
        }
        return 0
      })
      if (!this.purchasePrice && depotsSorted[0] && depotsSorted[0].purchasePrice) {
        this.purchasePrice = depotsSorted[0].purchasePrice.toFixed(3) || 0
      }
      if (this.product.purchasePrice !== null && this.product.purchasePrice !== 0) {
        this.purchasePrice = this.product.purchasePrice.toFixed(3) || 0
        if (this.product.purchaseCurrency) {
          this.selectedCurrency = this.product.purchaseCurrency.id
        }
      }
      if (!this.selectedSupplier && depotsSorted[0] && depotsSorted[0].supplier) {
        this.selectedSupplier = depotsSorted[0].supplier.id
      }
      if (!this.quantity && depotsSorted[0] && depotsSorted[0].quantityStock) {
        this.quantity = Number(depotsSorted[0].quantityStock.toFixed(0))
      }
      if (!this.position1 && depotsSorted[0] && depotsSorted[0].position1) {
        this.position1 = depotsSorted[0].position1
      }
      if (!this.position2 && depotsSorted[0] && depotsSorted[0].position2) {
        this.position2 = depotsSorted[0].position2
      }
      if (!this.position3 && depotsSorted[0] && depotsSorted[0].position3) {
        this.position3 = depotsSorted[0].position3
      }
    } else {
      // never used product
      this.purchasePrice = '0.000'
      if (
        !isNaN(this.product.purchasePrice) &&
        this.product.purchasePrice !== undefined &&
        this.product.purchasePrice !== null &&
        this.product.purchasePrice !== 0
      ) {
        this.purchasePrice = this.product.purchasePrice.toFixed(3) || 0
        if (this.product.purchaseCurrency) {
          this.selectedCurrency = this.product.purchaseCurrency.id
        }
      }
      if (this.suppliers.length === 1) {
        this.selectedSupplier = this.suppliers[0].id
      }
    }
    if (this.operationToUpdate) {
      if (this.operationToUpdate.purchaseCurrency) {
        this.selectedCurrency = this.operationToUpdate.purchaseCurrency
      }
      if (this.operationToUpdate.purchaseCurrencyDate) {
        this.dateDUZP = this.operationToUpdate.purchaseCurrencyDate
      }
    }
  }

  toggleLockQuantity(): void {
    //   this.isQuantityLocked = !this.isQuantityLocked
    //   this.setViewSettings()
  }

  quantityChanged(event) {
    this.quantity = event
    let remainingQuantity = this.quantity
    this.productDepots.forEach(pDepot => {
      if (remainingQuantity >= pDepot.quantityStock) {
        pDepot.amountToUnload = pDepot.quantityStock
        remainingQuantity -= pDepot.quantityStock
      } else {
        pDepot.amountToUnload = remainingQuantity
        remainingQuantity = 0
      }
    })
  }

  allPiecesSelected(pD) {
    this.quantity = pD.quantityStock
  }

  amountToUnloadChanged(event, productDepot) {
    productDepot.amountToUnload = event
    this.quantity = this.productDepots.reduce((total, pd) => {
      return Number(pd.amountToUnload) + Number(total)
    }, 0)
  }

  getThumbUrl(mainImage) {
    return mainImage?.thumbnails?.length ? mainImage?.thumbnails?.filter(t => t.format === 'w135wp')[0].url : ''
  }
}
